import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import api from "../services/api";

// Import logo - place your logo files in src/assets/
import uptechLogo from "../assets/uptech-logo.png";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: ""
  });

  // Check for success message from registration
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setForm(prev => ({ ...prev, email: location.state.email }));
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!form.email || !form.password) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);

    try {
      const response = await api.post("/Auth/login", {
        email: form.email,
        password: form.password
      });

      localStorage.setItem("token", response.data.token);
      localStorage.setItem("user", JSON.stringify(response.data.user || response.data));

      if (response.data.user?.isAdmin || response.data.isAdmin) {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }

    } catch (err) {
      console.error("Login error:", err);
      const message = err.response?.data?.message
        || err.response?.data
        || "Login failed. Please check your credentials.";
      setError(typeof message === 'string' ? message : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#011745",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
      fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif"
    },
    backgroundGlow1: {
      position: "absolute",
      top: "10%",
      right: "-20%",
      width: "60%",
      height: "60%",
      background: "radial-gradient(ellipse at center, rgba(17, 131, 251, 0.15) 0%, transparent 70%)",
      pointerEvents: "none"
    },
    backgroundGlow2: {
      position: "absolute",
      bottom: "-10%",
      left: "-10%",
      width: "50%",
      height: "50%",
      background: "radial-gradient(ellipse at center, rgba(88, 214, 190, 0.1) 0%, transparent 60%)",
      pointerEvents: "none"
    },
    card: {
      background: "white",
      borderRadius: "20px",
      padding: "48px 40px",
      width: "100%",
      maxWidth: "440px",
      boxShadow: "0 25px 50px rgba(0,0,0,0.3)",
      position: "relative",
      zIndex: 1
    },
    logo: {
      textAlign: "center",
      marginBottom: "32px"
    },
    logoImage: {
      height: "50px",
      width: "auto"
    },
    title: {
      fontSize: "28px",
      fontWeight: "800",
      color: "#011745",
      marginBottom: "8px",
      textAlign: "center"
    },
    subtitle: {
      color: "#64748b",
      textAlign: "center",
      marginBottom: "32px",
      fontSize: "15px"
    },
    formGroup: {
      marginBottom: "24px"
    },
    label: {
      display: "block",
      fontWeight: "600",
      color: "#011745",
      marginBottom: "8px",
      fontSize: "14px"
    },
    inputWrapper: {
      position: "relative"
    },
    input: {
      width: "100%",
      padding: "16px 20px",
      paddingLeft: "48px",
      border: "2px solid #e8edf5",
      borderRadius: "12px",
      fontSize: "15px",
      transition: "all 0.3s ease",
      boxSizing: "border-box",
      background: "#f7f9fc",
      color: "#011745",
      fontFamily: "inherit"
    },
    inputIcon: {
      position: "absolute",
      left: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      color: "#94a3b8",
      width: "20px",
      height: "20px"
    },
    passwordToggle: {
      position: "absolute",
      right: "16px",
      top: "50%",
      transform: "translateY(-50%)",
      background: "none",
      border: "none",
      color: "#94a3b8",
      cursor: "pointer",
      padding: "4px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    },
    button: {
      width: "100%",
      padding: "16px",
      background: "linear-gradient(135deg, #1183FB 0%, #0d6edb 100%)",
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "700",
      cursor: "pointer",
      transition: "all 0.3s ease",
      fontFamily: "inherit"
    },
    buttonDisabled: {
      opacity: 0.7,
      cursor: "not-allowed"
    },
    error: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      color: "#dc2626",
      padding: "14px 16px",
      borderRadius: "12px",
      marginBottom: "24px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    success: {
      background: "#f0fdf4",
      border: "1px solid #bbf7d0",
      color: "#16a34a",
      padding: "14px 16px",
      borderRadius: "12px",
      marginBottom: "24px",
      fontSize: "14px",
      display: "flex",
      alignItems: "center",
      gap: "10px"
    },
    link: {
      color: "#1183FB",
      textDecoration: "none",
      fontWeight: "600"
    },
    footer: {
      textAlign: "center",
      marginTop: "28px",
      color: "#64748b",
      fontSize: "14px"
    },
    forgotPassword: {
      textAlign: "right",
      marginBottom: "24px"
    },
    spinner: {
      display: "inline-block",
      width: "20px",
      height: "20px",
      border: "2px solid rgba(255,255,255,0.3)",
      borderRadius: "50%",
      borderTopColor: "white",
      animation: "spin 0.8s linear infinite",
      marginRight: "10px",
      verticalAlign: "middle"
    },
    divider: {
      display: "flex",
      alignItems: "center",
      margin: "28px 0",
      gap: "16px"
    },
    dividerLine: {
      flex: 1,
      height: "1px",
      background: "#e8edf5"
    },
    dividerText: {
      color: "#94a3b8",
      fontSize: "13px",
      fontWeight: "500"
    },
    biometricBtn: {
      width: "100%",
      padding: "14px",
      background: "white",
      border: "2px solid #e8edf5",
      borderRadius: "12px",
      fontSize: "14px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "10px",
      color: "#011745",
      transition: "all 0.3s ease",
      fontFamily: "inherit"
    }
  };

  return (
    <div style={styles.container}>
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          input:focus {
            border-color: #1183FB !important;
            box-shadow: 0 0 0 4px rgba(17, 131, 251, 0.1) !important;
            outline: none !important;
            background: white !important;
          }
          button:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 24px rgba(17, 131, 251, 0.35);
          }
          .biometric-btn:hover {
            border-color: #1183FB !important;
            background: rgba(17, 131, 251, 0.05) !important;
          }
        `}
      </style>

      <div style={styles.backgroundGlow1}></div>
      <div style={styles.backgroundGlow2}></div>

      <div style={styles.card}>
        <div style={styles.logo}>
          <img src={uptechLogo} alt="Uptech Innovations Ltd" style={styles.logoImage} />
        </div>
        <h1 style={styles.title}>Welcome Back</h1>
        <p style={styles.subtitle}>Sign in to your Uptech account</p>

        {successMessage && (
          <div style={styles.success}>
            <span>✓</span>
            <span>{successMessage}</span>
          </div>
        )}

        {error && (
          <div style={styles.error}>
            <span>!</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label style={styles.label}>Email or Phone</label>
            <div style={styles.inputWrapper}>
              <input
                type="text"
                name="email"
                style={styles.input}
                placeholder="Enter your email or phone"
                value={form.email}
                onChange={handleChange}
                disabled={loading}
                required
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                <polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Password</label>
            <div style={styles.inputWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                style={styles.input}
                placeholder="Enter your password"
                value={form.password}
                onChange={handleChange}
                disabled={loading}
                required
              />
              <svg style={styles.inputIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <button 
                type="button" 
                style={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div style={styles.forgotPassword}>
            <Link to="/forgot-password" style={{ ...styles.link, fontSize: "14px" }}>
              Forgot Password?
            </Link>
          </div>

          <button
            type="submit"
            style={{ ...styles.button, ...(loading ? styles.buttonDisabled : {}) }}
            disabled={loading}
          >
            {loading ? (
              <>
                <span style={styles.spinner}></span>
                Signing In...
              </>
            ) : (
              "Sign In"
            )}
          </button>
        </form>

        <div style={styles.divider}>
          <div style={styles.dividerLine}></div>
          <span style={styles.dividerText}>or continue with</span>
          <div style={styles.dividerLine}></div>
        </div>

        <button className="biometric-btn" style={styles.biometricBtn}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1183FB" strokeWidth="1.5">
            <path d="M12 11c0 1.657-1.343 3-3 3s-3-1.343-3-3c0-1.304.837-2.403 2-2.816V6a1 1 0 1 1 2 0v2.184c1.163.413 2 1.512 2 2.816z"/>
            <path d="M17 11c0 2.761-2.239 5-5 5s-5-2.239-5-5"/>
            <path d="M19 11c0 3.866-3.134 7-7 7s-7-3.134-7-7"/>
          </svg>
          Use Biometrics
        </button>

        <div style={styles.footer}>
          Don't have an account?{" "}
          <Link to="/register" style={styles.link}>Create Account</Link>
        </div>
      </div>
    </div>
  );
}
